import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Pagination } from '../components/Pagination';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ClickToCall } from '@/components/ClickToCall';
import { Linkedin } from 'lucide-react';
import { toast } from 'sonner';
import QuickActivityLog from '@/components/QuickActivityLog';

// Lead status columns with top color bars (matching Salesflare style)
const LEAD_STAGES = [
  { id: 'new', label: 'Lead', color: '#EF4444', bgColor: 'bg-red-500', headerBg: 'bg-white dark:bg-slate-800', borderColor: 'border-slate-200 dark:border-slate-700' },
  { id: 'contacted', label: 'Contacted', color: '#F59E0B', bgColor: 'bg-amber-500', headerBg: 'bg-white dark:bg-slate-800', borderColor: 'border-slate-200 dark:border-slate-700' },
  { id: 'qualified', label: 'Qualified', color: '#84CC16', bgColor: 'bg-lime-500', headerBg: 'bg-white dark:bg-slate-800', borderColor: 'border-slate-200 dark:border-slate-700' },
  { id: 'meeting', label: 'Meeting', color: '#22C55E', bgColor: 'bg-green-500', headerBg: 'bg-white dark:bg-slate-800', borderColor: 'border-slate-200 dark:border-slate-700' },
  { id: 'proposal', label: 'Proposal made', color: '#10B981', bgColor: 'bg-emerald-500', headerBg: 'bg-white dark:bg-slate-800', borderColor: 'border-slate-200 dark:border-slate-700' },
  { id: 'closed_won', label: 'Won', color: '#0D9488', bgColor: 'bg-teal-600', headerBg: 'bg-white dark:bg-slate-800', borderColor: 'border-slate-200 dark:border-slate-700' },
  { id: 'closed_lost', label: 'Lost', color: '#6B7280', bgColor: 'bg-gray-500', headerBg: 'bg-white dark:bg-slate-800', borderColor: 'border-slate-200 dark:border-slate-700' },
];

// Industry colors
const INDUSTRY_COLORS = {
  'Technology': { bg: 'bg-blue-100', text: 'text-blue-700' },
  'Healthcare': { bg: 'bg-green-100', text: 'text-green-700' },
  'Finance': { bg: 'bg-purple-100', text: 'text-purple-700' },
  'Education': { bg: 'bg-amber-100', text: 'text-amber-700' },
  'Retail': { bg: 'bg-pink-100', text: 'text-pink-700' },
  'default': { bg: 'bg-gray-100', text: 'text-gray-700' },
};

// Source colors (subtle dot indicator)
const SOURCE_COLORS = {
  'Website': 'bg-blue-400',
  'Referral': 'bg-green-400',
  'LinkedIn': 'bg-indigo-400',
  'Cold Call': 'bg-orange-400',
  'Email Campaign': 'bg-purple-400',
  'Import': 'bg-slate-400',
  'default': 'bg-gray-400',
};

// Info icon component
const InfoIcon = () => (
  <svg className="w-3.5 h-3.5 text-slate-400 ml-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const formatCurrency = (value) => {
  if (!value) return '₹0';
  if (value >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
  if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
  if (value >= 1000) return `₹${(value / 1000).toFixed(1)}K`;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(value);
};

// Clean KPI Card Component - matching dashboard exactly
const KPICard = ({ title, value, trend, subValue }) => (
  <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4 hover:shadow-sm transition-shadow">
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
        {title}
        <InfoIcon />
      </span>
      <button className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </button>
    </div>
    <div className="flex items-baseline gap-2">
      <span className="text-2xl font-bold text-slate-900 dark:text-white">{value}</span>
      {trend !== undefined && (
        <span className="inline-flex items-center text-xs font-medium text-green-600 dark:text-green-400">
          <svg className="w-3 h-3 mr-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
          {trend}
        </span>
      )}
    </div>
    {subValue && <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{subValue}</p>}
  </div>
);

// Lead Card Component - Clean white design with opportunity info
function LeadCard({ lead, isDragging, onClick, onAddOpportunity, onQuickLog }) {
  const industryColor = INDUSTRY_COLORS[lead.industry] || INDUSTRY_COLORS.default;
  const sourceColor = SOURCE_COLORS[lead.source] || SOURCE_COLORS.default;
  const oppCount = lead.opportunities_count || 0;
  const oppValue = lead.opportunities_value || 0;
  
  return (
    <div
      onClick={() => onClick(lead)}
      className={`
        bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-3 cursor-pointer
        transition-all duration-200 group
        ${isDragging ? 'shadow-xl scale-105 rotate-1 border-blue-400' : 'shadow-sm hover:shadow-md hover:border-slate-300 dark:hover:border-slate-600'}
      `}
      data-testid={`lead-card-${lead.id}`}
    >
      {/* Header Row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <div className={`w-2 h-2 rounded-full ${sourceColor}`} title={lead.source || 'Unknown Source'} />
          <span className="text-[10px] text-slate-400 dark:text-slate-500 uppercase">{lead.source || 'Unknown'}</span>
        </div>
        <div className="flex items-center gap-1">
          {/* Click-to-Call Icon */}
          {lead.phone && lead.phone !== '-' && (
            <div onClick={(e) => e.stopPropagation()}>
              <ClickToCall 
                phone={lead.phone} 
                leadId={lead.id} 
                leadName={lead.name}
                variant="icon"
                size="sm"
              />
            </div>
          )}
          {/* LinkedIn Icon */}
          {lead.linkedin_website && (
            <a
              href={lead.linkedin_website.startsWith('http') ? lead.linkedin_website : `https://www.linkedin.com/in/${lead.linkedin_website}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="p-0.5 text-[#0A66C2] hover:bg-[#0A66C2]/10 rounded transition-colors"
              title="View LinkedIn"
            >
              <Linkedin className="w-3.5 h-3.5" />
            </a>
          )}
          {lead.lead_score > 0 && (
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
              lead.lead_score >= 70 ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300' :
              lead.lead_score >= 40 ? 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300' :
              'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300'
            }`}>
              {lead.lead_score}
            </span>
          )}
        </div>
      </div>
      
      {/* Lead Name */}
      <h4 className="font-semibold text-slate-900 dark:text-white text-sm leading-tight mb-1 line-clamp-1 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
        {lead.name}
      </h4>
      
      {/* Organization */}
      <p className="text-xs text-slate-500 dark:text-slate-400 truncate mb-2">
        {lead.organization || 'No Company'}
      </p>

      {/* Source-file badge — shows the CSV/XLSX filename this lead came from */}
      {lead.import_source_name && (
        <div
          className="inline-flex items-center gap-1 px-2 py-0.5 mb-2 rounded bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-[10px] max-w-full"
          title={`Imported from ${lead.import_source_name}`}
          data-testid="lead-import-source-badge"
        >
          <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
          </svg>
          <span className="truncate">{lead.import_source_name}</span>
        </div>
      )}

      {/* Opportunities Badge */}
      {oppCount > 0 && (
        <div className="flex items-center gap-2 mb-2 px-2 py-1 bg-purple-50 dark:bg-purple-900/30 rounded">
          <svg className="w-3 h-3 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <span className="text-[10px] font-medium text-purple-700 dark:text-purple-300">
            {oppCount} Opp{oppCount > 1 ? 's' : ''} • {formatCurrency(oppValue)}
          </span>
        </div>
      )}
      
      {/* Bottom Row */}
      <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-700">
        {lead.industry ? (
          <span className={`text-[10px] font-medium px-2 py-0.5 rounded ${industryColor.bg} ${industryColor.text} truncate max-w-[80px]`}>
            {lead.industry}
          </span>
        ) : <span />}
        {lead.deal_value > 0 && (
          <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
            {formatCurrency(lead.deal_value)}
          </span>
        )}
      </div>

      {/* NAD Indicator */}
      {lead.next_action_date && (
        (() => {
          const nadDate = new Date(lead.next_action_date);
          const today = new Date(); today.setHours(0,0,0,0);
          const diffDays = Math.floor((nadDate - today) / (1000*60*60*24));
          const isOverdue = diffDays < 0;
          const isToday = diffDays === 0;
          const color = isOverdue ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800'
            : isToday ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800'
            : 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800';
          return (
            <div className={`mt-1.5 px-2 py-1 rounded border text-[10px] font-medium flex items-center gap-1 ${color}`} data-testid={`nad-indicator-${lead.id}`}>
              <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
              <span>NAD: {nadDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
              {isOverdue && <span className="font-bold">({Math.abs(diffDays)}d overdue)</span>}
              {isToday && <span className="font-bold">(Today)</span>}
            </div>
          );
        })()
      )}

      {/* Action Buttons - shown on hover */}
      <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-700 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onQuickLog && onQuickLog({ lead_id: lead.id, lead_name: lead.name, activity_type: 'call' });
          }}
          className="flex-1 text-[10px] font-medium text-slate-600 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 flex items-center justify-center gap-1 py-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          data-testid={`quick-log-btn-${lead.id}`}
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          Log
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onAddOpportunity(lead);
          }}
          className="flex-1 text-[10px] font-medium text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 flex items-center justify-center gap-1 py-1 rounded hover:bg-purple-50 dark:hover:bg-purple-900/30 transition-colors"
          data-testid={`add-opportunity-btn-${lead.id}`}
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          + Opp
        </button>
      </div>
      
      {/* Owner Info */}
      {lead.owner_name && (
        <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-slate-100 dark:border-slate-700">
          <div className="w-5 h-5 rounded-full bg-slate-200 dark:bg-slate-600 flex items-center justify-center text-slate-600 dark:text-slate-300 text-[10px] font-semibold">
            {lead.owner_name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <span className="text-[10px] text-slate-500 dark:text-slate-400 truncate flex-1" title={`Owner: ${lead.owner_name}`}>
            {lead.owner_name}
          </span>
        </div>
      )}
    </div>
  );
}

// Sortable Wrapper
function SortableLeadCard({ lead, onClick, onAddOpportunity, onQuickLog }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: lead.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} className="touch-none cursor-grab active:cursor-grabbing">
      <LeadCard lead={lead} isDragging={isDragging} onClick={onClick} onAddOpportunity={onAddOpportunity} onQuickLog={onQuickLog} />
    </div>
  );
}

// Column Component - White background with colored top bar only
function LeadColumn({ stage, leads, onLeadClick, onAddOpportunity, onQuickLog, isOver, meta, onLoadMore, isLoadingMore }) {
  const { setNodeRef } = useDroppable({ id: stage.id });
  const leadIds = useMemo(() => leads.map(l => l.id), [leads]);
  // Prefer server-side aggregates when provided (so header reflects ALL leads,
  // not just the subset currently loaded in the column)
  const totalCount = meta?.total ?? leads.length;
  const totalValue = meta?.pipeline_value ?? leads.reduce((sum, l) => sum + (l.deal_value || 0), 0);
  const hasMore = !!meta?.has_more;
  const remaining = Math.max(0, (meta?.total || 0) - leads.length);

  return (
    <div
      ref={setNodeRef}
      className={`flex-shrink-0 w-72 flex flex-col bg-white dark:bg-slate-800 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 transition-all duration-200 ${isOver ? 'ring-2 ring-blue-400 ring-offset-2' : ''}`}
      data-testid={`column-${stage.id}`}
    >
      {/* Colored top bar - thin line only */}
      <div className={`h-1.5 ${stage.bgColor}`} />
      
      {/* Column Header - White background */}
      <div className="sticky top-0 z-10 bg-white dark:bg-slate-800 border-b border-slate-100 dark:border-slate-700 p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-slate-800 dark:text-slate-100 text-sm">{stage.label}</h3>
            <span className="text-xs text-slate-400 dark:text-slate-500 font-medium" data-testid={`column-count-${stage.id}`}>
              {meta ? `${leads.length} / ${totalCount}` : leads.length}
            </span>
          </div>
        </div>
        <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{formatCurrency(totalValue)}</p>
      </div>

      {/* Cards Container - White background */}
      <div className="flex-1 p-2 overflow-y-auto min-h-[300px] max-h-[calc(100vh-380px)] space-y-2 bg-white dark:bg-slate-800">
        <SortableContext items={leadIds} strategy={verticalListSortingStrategy}>
          {leads.map(lead => (
            <SortableLeadCard key={lead.id} lead={lead} onClick={onLeadClick} onAddOpportunity={onAddOpportunity} onQuickLog={onQuickLog} />
          ))}
        </SortableContext>
        {leads.length === 0 && (
          <div className={`h-32 border-2 border-dashed rounded-lg flex flex-col items-center justify-center transition-colors duration-200 ${isOver ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/30' : 'border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900'}`}>
            <svg className="w-8 h-8 text-slate-300 dark:text-slate-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <p className="text-xs text-slate-400 dark:text-slate-500">Drop leads here</p>
          </div>
        )}
        {hasMore && (
          <button
            onClick={() => onLoadMore && onLoadMore(stage.id)}
            disabled={isLoadingMore}
            data-testid={`load-more-${stage.id}`}
            className="w-full mt-2 px-3 py-2 text-xs font-medium rounded-lg border border-dashed border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 transition-colors"
          >
            {isLoadingMore ? 'Loading…' : `Load more (${remaining} remaining)`}
          </button>
        )}
      </div>
    </div>
  );
}

// Lead Detail Drawer - Clean styling
function LeadDrawer({ lead, isOpen, onClose, onUpdate, stages }) {
  const [editedLead, setEditedLead] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (lead) setEditedLead(lead);
  }, [lead]);

  const handleSave = async () => {
    if (!editedLead) return;
    setSaving(true);
    try {
      await onUpdate(editedLead);
      onClose();
    } catch (error) {
      console.error('Error updating lead:', error);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !lead || !editedLead) return null;

  const industryColor = INDUSTRY_COLORS[lead.industry] || INDUSTRY_COLORS.default;
  const currentStage = stages.find(s => s.id === editedLead.status) || stages.find(s => s.id === lead.status);

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
          <div className="flex items-center justify-between">
            <div 
              className="text-xs font-semibold uppercase tracking-wide px-2 py-1 rounded"
              style={{ backgroundColor: `${currentStage?.color}15`, color: currentStage?.color }}
            >
              {currentStage?.label || lead.status}
            </div>
            <button onClick={onClose} className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors">
              <svg className="w-5 h-5 text-slate-500 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white mt-3">{lead.name}</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">{lead.organization || 'No Company'}</p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
              <p className="text-xs text-slate-500 dark:text-slate-400 font-medium uppercase">Deal Value</p>
              <p className="text-xl font-bold text-slate-900 dark:text-white mt-1">{formatCurrency(lead.deal_value)}</p>
            </div>
            <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
              <p className="text-xs text-slate-500 dark:text-slate-400 font-medium uppercase">Lead Score</p>
              <p className="text-xl font-bold text-slate-900 dark:text-white mt-1">{lead.lead_score || 0}</p>
            </div>
          </div>

          {/* Contact Details */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-4">Contact Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-400 block mb-1">Email</label>
                <p className="text-sm text-slate-700 dark:text-slate-300">{lead.email || '-'}</p>
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Phone</label>
                <div className="flex items-center gap-2">
                  <p className="text-sm text-slate-700 dark:text-slate-300">{lead.phone || '-'}</p>
                  {lead.phone && lead.phone !== '-' && (
                    <ClickToCall 
                      phone={lead.phone} 
                      leadId={lead.id} 
                      leadName={lead.name}
                      variant="icon"
                      size="sm"
                    />
                  )}
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Industry</label>
                {lead.industry ? (
                  <span className={`text-xs font-medium px-2 py-1 rounded ${industryColor.bg} ${industryColor.text}`}>
                    {lead.industry}
                  </span>
                ) : <span className="text-sm text-slate-400">Not set</span>}
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Source</label>
                <p className="text-sm text-slate-700 dark:text-slate-300 capitalize">{lead.source || '-'}</p>
              </div>
              {lead.import_source_name && (
                <div className="col-span-2">
                  <label className="text-xs text-slate-400 block mb-1">Imported From</label>
                  <span
                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-xs"
                    data-testid="lead-detail-import-source"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                    {lead.import_source_name}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Change Status */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3">Change Status</h3>
            <div className="flex flex-wrap gap-2">
              {stages.map(stage => (
                <button
                  key={stage.id}
                  onClick={() => setEditedLead({ ...editedLead, status: stage.id })}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                    editedLead.status === stage.id 
                      ? 'border-transparent text-white shadow-sm' 
                      : 'border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-500 bg-white dark:bg-slate-700'
                  }`}
                  style={editedLead.status === stage.id ? { backgroundColor: stage.color } : {}}
                >
                  {stage.label}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          {lead.remarks && (
            <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
              <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Notes</h3>
              <p className="text-sm text-slate-600 dark:text-slate-300">{lead.remarks}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 flex items-center justify-between">
          <Link to={`/leads/${lead.id}`} className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium">
            View Full Details →
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving} className="px-4 py-2 text-sm font-medium text-white bg-slate-900 dark:bg-slate-100 dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-white rounded-lg transition-colors disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
      </div>
    </>
  );
}

// List View Component with Pagination
function LeadsListView({ leads, onLeadClick, onAddOpportunity, onQuickLog, stages, formatCurrency, onDeleteLead, onBulkDelete }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Sort leads
  const sortedLeads = useMemo(() => {
    return [...leads].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortField === 'deal_value') {
        aVal = aVal || 0;
        bVal = bVal || 0;
      } else if (sortField === 'created_at') {
        aVal = new Date(aVal || 0);
        bVal = new Date(bVal || 0);
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });
  }, [leads, sortField, sortOrder]);

  // Pagination
  const totalPages = Math.ceil(sortedLeads.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedLeads = sortedLeads.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedLeads(paginatedLeads.map(l => l.id));
    } else {
      setSelectedLeads([]);
    }
  };

  const handleSelectLead = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const getStageInfo = (status) => stages.find(s => s.id === status) || stages[0];

  const SortIcon = ({ field }) => (
    <span className="ml-1 inline-flex flex-col">
      <svg className={`w-2.5 h-2.5 ${sortField === field && sortOrder === 'asc' ? 'text-slate-900 dark:text-white' : 'text-slate-300 dark:text-slate-600'}`} fill="currentColor" viewBox="0 0 20 20">
        <path d="M5 12l5-5 5 5H5z"/>
      </svg>
      <svg className={`w-2.5 h-2.5 -mt-1 ${sortField === field && sortOrder === 'desc' ? 'text-slate-900 dark:text-white' : 'text-slate-300 dark:text-slate-600'}`} fill="currentColor" viewBox="0 0 20 20">
        <path d="M5 8l5 5 5-5H5z"/>
      </svg>
    </span>
  );

  const handleDeleteSingle = async (leadId, leadName, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete lead "${leadName}"? This cannot be undone.`)) return;
    setDeletingId(leadId);
    try {
      await onDeleteLead(leadId);
      setSelectedLeads(prev => prev.filter(id => id !== leadId));
    } finally {
      setDeletingId(null);
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedLeads.length} selected lead(s)? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      await onBulkDelete(selectedLeads);
      setSelectedLeads([]);
    } finally {
      setBulkDeleting(false);
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid="leads-list-view">
      {/* Bulk Actions Bar */}
      {selectedLeads.length > 0 && (
        <div className="px-4 py-2.5 bg-blue-50 dark:bg-blue-900/30 border-b border-blue-200 dark:border-blue-800 flex items-center justify-between" data-testid="bulk-actions-bar">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
              {selectedLeads.length} lead{selectedLeads.length > 1 ? 's' : ''} selected
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              data-testid="bulk-delete-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              {bulkDeleting ? 'Deleting...' : `Delete ${selectedLeads.length}`}
            </button>
            <button
              onClick={() => setSelectedLeads([])}
              className="px-3 py-1.5 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              data-testid="clear-selection-btn"
            >
              Clear
            </button>
          </div>
        </div>
      )}
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1400px]">
          <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
            <tr>
              <th className="w-10 px-4 py-3">
                <input 
                  type="checkbox" 
                  checked={selectedLeads.length === paginatedLeads.length && paginatedLeads.length > 0}
                  onChange={handleSelectAll}
                  className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
                />
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wide cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800"
                onClick={() => handleSort('name')}
              >
                <span className="flex items-center">Name <SortIcon field="name" /></span>
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wide cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800"
                onClick={() => handleSort('organization')}
              >
                <span className="flex items-center">Company <SortIcon field="organization" /></span>
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wide cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800"
                onClick={() => handleSort('status')}
              >
                <span className="flex items-center">Status <SortIcon field="status" /></span>
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wide cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800"
                onClick={() => handleSort('next_action_note')}
              >
                <span className="flex items-center">Next Action <SortIcon field="next_action_note" /></span>
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wide cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800"
                onClick={() => handleSort('next_action_date')}
              >
                <span className="flex items-center">NAD <SortIcon field="next_action_date" /></span>
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wide">
                Delay
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wide cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800"
                onClick={() => handleSort('owner_name')}
              >
                <span className="flex items-center">Lead Owner <SortIcon field="owner_name" /></span>
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wide cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800"
                onClick={() => handleSort('deal_value')}
              >
                <span className="flex items-center">Value <SortIcon field="deal_value" /></span>
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wide cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800"
                onClick={() => handleSort('industry')}
              >
                <span className="flex items-center">Industry <SortIcon field="industry" /></span>
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wide cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800"
                onClick={() => handleSort('source')}
              >
                <span className="flex items-center">Source <SortIcon field="source" /></span>
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wide cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800"
                onClick={() => handleSort('created_at')}
              >
                <span className="flex items-center">Created <SortIcon field="created_at" /></span>
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wide">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
            {paginatedLeads.map(lead => {
              const stageInfo = getStageInfo(lead.status);
              const industryColor = INDUSTRY_COLORS[lead.industry] || INDUSTRY_COLORS.default;
              return (
                <tr 
                  key={lead.id} 
                  className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors cursor-pointer"
                  onClick={() => onLeadClick(lead)}
                  data-testid={`lead-row-${lead.id}`}
                >
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <input 
                      type="checkbox" 
                      checked={selectedLeads.includes(lead.id)}
                      onChange={() => handleSelectLead(lead.id)}
                      className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-600 flex items-center justify-center text-slate-700 dark:text-slate-200 text-sm font-semibold flex-shrink-0">
                        {lead.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{lead.name}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{lead.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-slate-700 dark:text-slate-300 truncate max-w-[150px]">{lead.organization || '-'}</p>
                    {lead.import_source_name && (
                      <span
                        className="inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 rounded bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-[10px] max-w-[150px]"
                        title={`Imported from ${lead.import_source_name}`}
                      >
                        <svg className="w-2.5 h-2.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        <span className="truncate">{lead.import_source_name}</span>
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span 
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full text-white"
                      style={{ backgroundColor: stageInfo.color }}
                    >
                      {stageInfo.label}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-slate-700 dark:text-slate-300 truncate block max-w-[200px]" title={lead.next_action_note || ''}>
                      {lead.next_action_note || <span className="text-slate-400 italic">— not set</span>}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="text-sm text-slate-700 dark:text-slate-300">
                      {lead.next_action_date
                        ? new Date(lead.next_action_date + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' })
                        : <span className="text-slate-400">—</span>}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {(() => {
                      const d = computeDelay(lead.next_action_date);
                      return (
                        <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded-full ${DELAY_TONE_CLASSES[d.tone]}`}>
                          {d.label}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <OwnerAvatar name={lead.owner_name} size={28} />
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-slate-800 dark:text-slate-200 truncate">{lead.owner_name || 'Unassigned'}</p>
                        {lead.owner_role && (
                          <p className="text-[10px] text-slate-500 dark:text-slate-400 capitalize truncate">{lead.owner_role.replace(/_/g, ' ')}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-semibold text-slate-900 dark:text-white">{formatCurrency(lead.deal_value)}</span>
                  </td>
                  <td className="px-4 py-3">
                    {lead.industry ? (
                      <span className={`text-xs font-medium px-2 py-1 rounded ${industryColor.bg} ${industryColor.text}`}>
                        {lead.industry}
                      </span>
                    ) : <span className="text-xs text-slate-400 dark:text-slate-500">-</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-slate-600 dark:text-slate-400 capitalize">{lead.source || '-'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-slate-500 dark:text-slate-400">
                      {lead.created_at ? new Date(lead.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' }) : '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      <Link 
                        to={`/leads/${lead.id}`} 
                        className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-colors"
                        title="View"
                        data-testid={`view-lead-${lead.id}`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </Link>
                      <button
                        onClick={() => onQuickLog && onQuickLog({ lead_id: lead.id, lead_name: lead.name, activity_type: 'call' })}
                        className="p-1.5 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
                        title="Quick Log Activity"
                        data-testid={`quick-log-list-${lead.id}`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                        </svg>
                      </button>
                      <button
                        onClick={() => onAddOpportunity(lead)}
                        className="p-1.5 text-slate-400 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded transition-colors"
                        title="Convert to Opportunity"
                        data-testid={`add-opp-list-${lead.id}`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                      </button>
                      <Link
                        to={`/leads/${lead.id}?edit=true`}
                        className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded transition-colors"
                        title="Edit"
                        data-testid={`edit-lead-${lead.id}`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </Link>
                      <button
                        onClick={(e) => handleDeleteSingle(lead.id, lead.name, e)}
                        disabled={deletingId === lead.id}
                        className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors disabled:opacity-50"
                        title="Delete"
                        data-testid={`delete-lead-${lead.id}`}
                      >
                        {deletingId === lead.id ? (
                          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {paginatedLeads.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-12 text-center">
                  <div className="flex flex-col items-center">
                    <svg className="w-12 h-12 text-slate-300 dark:text-slate-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <p className="text-slate-500 dark:text-slate-400">No leads found</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      {sortedLeads.length > 0 && (
        <div className="px-4 py-3 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3 text-sm text-slate-600 dark:text-slate-400">
            <span>Show</span>
            <select 
              value={itemsPerPage} 
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
              className="px-2 py-1 border border-slate-200 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-slate-400"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span>per page</span>
            <span className="text-slate-400 dark:text-slate-500">|</span>
            <span>
              Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, sortedLeads.length)} of {sortedLeads.length} leads
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="px-2 py-1 text-sm border border-slate-200 dark:border-slate-600 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              First
            </button>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-2 py-1 text-sm border border-slate-200 dark:border-slate-600 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4 text-slate-700 dark:text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            {/* Page numbers */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-3 py-1 text-sm border rounded-md transition-colors ${
                    currentPage === pageNum 
                      ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 border-slate-900 dark:border-white' 
                      : 'border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-2 py-1 text-sm border border-slate-200 dark:border-slate-600 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4 text-slate-700 dark:text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="px-2 py-1 text-sm border border-slate-200 dark:border-slate-600 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Last
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Lead scope filter tabs ─────────────────────────────────────────────
// Drives the "All / Mine / Team" pill group above the list + kanban.
// Role-aware default: managers+ start on "all", ICs start on "mine".
const MANAGER_ROLES = new Set([
  'admin', 'company_sales_head', 'country_sales_head', 'regional_sales_head',
  'solutions_architect', 'manager', 'team_leader',
]);

// ─── Delay pill helper (for Next Action Date column) ────────────────────
// Returns { tone, label } where tone maps to a Tailwind class pair.
function computeDelay(nextActionDate) {
  if (!nextActionDate) return { tone: 'slate', label: 'no action' };
  // nextActionDate is YYYY-MM-DD from backend
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const nad = new Date(nextActionDate + 'T00:00:00');
  const diffDays = Math.round((nad - today) / 86400000);
  if (diffDays > 0) return { tone: 'emerald', label: `in ${diffDays}d` };
  if (diffDays === 0) return { tone: 'blue', label: 'due today' };
  if (diffDays === -1) return { tone: 'rose', label: '1 day overdue' };
  return { tone: 'rose', label: `${Math.abs(diffDays)} days overdue` };
}
const DELAY_TONE_CLASSES = {
  slate:   'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400',
  emerald: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  blue:    'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  rose:    'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
};

// Colored-initial avatar for Lead Owner column (matches Suggestions panel style)
const _OWNER_TONES = [
  'bg-amber-400 text-white', 'bg-rose-400 text-white', 'bg-emerald-400 text-white',
  'bg-sky-400 text-white',   'bg-indigo-400 text-white', 'bg-purple-400 text-white',
  'bg-orange-400 text-white', 'bg-teal-400 text-white',
];
function _ownerTone(seed = '') {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h << 5) - h + seed.charCodeAt(i);
  return _OWNER_TONES[Math.abs(h) % _OWNER_TONES.length];
}
function OwnerAvatar({ name, size = 28 }) {
  const initial = (name || '?').replace(/[^a-zA-Z]/g, '').slice(0, 1).toUpperCase() || '?';
  const px = size + 'px';
  return (
    <span className={`inline-flex items-center justify-center rounded-full font-semibold flex-shrink-0 ${_ownerTone(name)}`} style={{ width: px, height: px, fontSize: Math.round(size * 0.42) }}>
      {initial}
    </span>
  );
}

// Main Component
export default function LeadsKanban() {
  const location = useLocation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const isManagerOrAbove = MANAGER_ROLES.has(user?.role);
  // Role-aware default: managers start on "all", everyone else on "mine"
  const defaultScope = isManagerOrAbove ? 'all' : 'mine';
  const [scope, setScope] = useState(searchParams.get('scope') || defaultScope);

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [industryFilter, setIndustryFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' or 'list'
  const [showAddOppModal, setShowAddOppModal] = useState(false);
  const [selectedLeadForOpp, setSelectedLeadForOpp] = useState(null);
  const [quickLogContext, setQuickLogContext] = useState(null);
  const [listPage, setListPage] = useState(1);
  const [listLimit, setListLimit] = useState(20);
  const [listPagination, setListPagination] = useState(null);
  const [listLeads, setListLeads] = useState([]);
  const [stageMeta, setStageMeta] = useState({});
  const [loadingStage, setLoadingStage] = useState(null);
  const STAGE_LIMIT = 100;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ stage_limit: STAGE_LIMIT });
      if (scope && scope !== 'all') params.append('scope', scope);
      const response = await axios.get(`${API}/leads/kanban?${params.toString()}`);
      const stages = response.data?.stages || {};
      const allLeads = [];
      const meta = {};
      Object.entries(stages).forEach(([st, d]) => {
        meta[st] = {
          total: d.total || 0,
          loaded: d.loaded || 0,
          has_more: !!d.has_more,
          page: 1,
          pipeline_value: d.pipeline_value || 0,
        };
        (d.leads || []).forEach((l) => allLeads.push(l));
      });
      setStageMeta(meta);
      // Iter_109 PERF: kanban endpoint now returns opportunities_count/value
      // inline. Eliminates the prior N HTTP requests via enrichWithOpps.
      setLeads(allLeads);
    } catch (error) {
      console.error('Error fetching kanban leads:', error);
    } finally {
      setLoading(false);
    }
  }, [scope]);

  const loadMoreStage = useCallback(async (stageId) => {
    const meta = stageMeta[stageId];
    if (!meta || !meta.has_more || loadingStage) return;
    setLoadingStage(stageId);
    try {
      const nextPage = (meta.page || 1) + 1;
      const params = new URLSearchParams({ stage: stageId, stage_limit: STAGE_LIMIT, page: nextPage });
      if (scope && scope !== 'all') params.append('scope', scope);
      const response = await axios.get(`${API}/leads/kanban?${params.toString()}`);
      const newLeads = response.data?.leads || [];
      // Defensive de-dup: drop any lead whose id is already loaded.
      setLeads((prev) => {
        const existingIds = new Set(prev.map((l) => l.id));
        const fresh = newLeads.filter((l) => !existingIds.has(l.id));
        return [...prev, ...fresh];
      });
      setStageMeta((prev) => ({
        ...prev,
        [stageId]: {
          ...prev[stageId],
          loaded: (prev[stageId]?.loaded || 0) + newLeads.length,
          has_more: !!response.data?.has_more,
          page: nextPage,
        },
      }));
    } catch (error) {
      console.error('Error loading more leads for stage', stageId, error);
    } finally {
      setLoadingStage(null);
    }
  }, [stageMeta, loadingStage]);

  // Refetch when navigating back with refresh state
  useEffect(() => { fetchLeads(); }, [fetchLeads, location.state?.refresh]);

  // Paginated list fetch — only when in list view
  const fetchListLeads = useCallback(async () => {
    if (viewMode !== 'list') return;
    try {
      const params = new URLSearchParams({ page: listPage, limit: listLimit });
      if (searchQuery) params.append('search', searchQuery);
      if (scope && scope !== 'all') params.append('scope', scope);
      const response = await axios.get(`${API}/leads?${params.toString()}`);
      if (response.data?.pagination) {
        setListLeads(response.data.data || []);
        setListPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching paginated leads:', error);
    }
  }, [viewMode, listPage, listLimit, searchQuery, scope]);

  useEffect(() => { fetchListLeads(); }, [fetchListLeads]);

  // Sync scope → URL + refetch
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (scope === defaultScope) next.delete('scope');
    else next.set('scope', scope);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  const handleAddOpportunity = (lead) => {
    setSelectedLeadForOpp(lead);
    setShowAddOppModal(true);
  };

  const handleOppCreated = () => {
    setShowAddOppModal(false);
    setSelectedLeadForOpp(null);
    fetchLeads(); // Refresh to get updated opportunity counts
  };

  const handleDeleteLead = async (leadId) => {
    try {
      await axios.delete(`${API}/leads/${leadId}`);
      toast.success('Lead deleted');
      fetchLeads();
      fetchListLeads();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to delete lead. You may not have permission.';
      toast.error(msg);
    }
  };

  const handleBulkDeleteLeads = async (leadIds) => {
    try {
      const res = await axios.post(`${API}/leads/bulk`, { lead_ids: leadIds, operation: 'delete' });
      const deleted = res.data?.deleted || 0;
      fetchLeads();
      fetchListLeads();
      if (deleted < leadIds.length) {
        toast.warning(`Deleted ${deleted} of ${leadIds.length} leads. Some leads may belong to other users.`);
      } else {
        toast.success(`Deleted ${deleted} lead${deleted === 1 ? '' : 's'}`);
      }
    } catch (error) {
      const status = error.response?.status;
      if (status === 429) {
        toast.error('Too many requests. Please wait a moment and try again.');
      } else {
        const msg = error.response?.data?.detail || 'Failed to delete leads.';
        toast.error(msg);
      }
    }
  };

  const industries = useMemo(() => [...new Set(leads.map(l => l.industry).filter(Boolean))], [leads]);
  const sources = useMemo(() => [...new Set(leads.map(l => l.source).filter(Boolean))], [leads]);

  const filteredLeads = useMemo(() => {
    return leads.filter(lead => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!lead.name?.toLowerCase().includes(query) && !lead.organization?.toLowerCase().includes(query) && !lead.email?.toLowerCase().includes(query)) return false;
      }
      if (industryFilter !== 'all' && lead.industry !== industryFilter) return false;
      if (sourceFilter !== 'all' && lead.source !== sourceFilter) return false;
      return true;
    });
  }, [leads, searchQuery, industryFilter, sourceFilter]);

  const leadsByStage = useMemo(() => {
    const grouped = {};
    LEAD_STAGES.forEach(stage => {
      grouped[stage.id] = filteredLeads.filter(lead => lead.status === stage.id);
    });
    return grouped;
  }, [filteredLeads]);

  // KPI values — prefer server-side stageMeta aggregates so counts reflect
  // ALL leads (not just the subset loaded into the Kanban columns).
  const hasFilters = !!searchQuery || industryFilter !== 'all' || sourceFilter !== 'all';
  const metaTotals = useMemo(() => {
    let totalC = 0, totalV = 0, qualC = 0, wonC = 0;
    Object.entries(stageMeta || {}).forEach(([st, m]) => {
      totalC += m.total || 0;
      totalV += m.pipeline_value || 0;
      if (['qualified', 'meeting', 'proposal'].includes(st)) qualC += m.total || 0;
      if (st === 'closed_won') wonC += m.total || 0;
    });
    return { totalC, totalV, qualC, wonC };
  }, [stageMeta]);
  const totalLeads = hasFilters ? filteredLeads.length : (metaTotals.totalC || filteredLeads.length);
  const totalValue = hasFilters ? filteredLeads.reduce((sum, l) => sum + (l.deal_value || 0), 0) : (metaTotals.totalV || 0);
  const qualifiedLeads = hasFilters ? filteredLeads.filter(l => ['qualified', 'meeting', 'proposal'].includes(l.status)).length : metaTotals.qualC;
  const wonLeads = hasFilters ? filteredLeads.filter(l => l.status === 'closed_won').length : metaTotals.wonC;

  const handleDragStart = (event) => setActiveId(event.active.id);
  const handleDragOver = (event) => setOverId(event.over?.id || null);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    setOverId(null);
    if (!over) return;

    const activeLead = leads.find(l => l.id === active.id);
    if (!activeLead) return;

    let targetStage = LEAD_STAGES.find(s => s.id === over.id)?.id;
    if (!targetStage) {
      const overLead = leads.find(l => l.id === over.id);
      if (overLead) targetStage = overLead.status;
    }

    if (targetStage && targetStage !== activeLead.status) {
      setLeads(prev => prev.map(l => l.id === active.id ? { ...l, status: targetStage } : l));
      try {
        await axios.put(`${API}/leads/${active.id}`, { ...activeLead, status: targetStage });
      } catch (error) {
        console.error('Error updating lead status:', error);
        fetchLeads();
      }
    }
  };

  const handleLeadClick = (lead) => {
    setSelectedLead(lead);
    setDrawerOpen(true);
  };

  const handleLeadUpdate = async (updatedLead) => {
    await axios.put(`${API}/leads/${updatedLead.id}`, updatedLead);
    setLeads(prev => prev.map(l => l.id === updatedLead.id ? updatedLead : l));
  };

  const activeLead = activeId ? leads.find(l => l.id === activeId) : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-10 h-10 border-4 border-slate-200 border-t-slate-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-slate-50 dark:bg-slate-900 -m-4 lg:-m-6" data-testid="leads-kanban-page">
      {/* Header Section */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 p-4 lg:p-6 space-y-4">
        {/* Title Row */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Leads</h1>
            <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          <div className="flex items-center gap-3">
            {/* View Toggle */}
            <div className="flex items-center bg-slate-100 dark:bg-slate-700 rounded-lg p-1" data-testid="view-toggle">
              <button
                onClick={() => setViewMode('kanban')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                  viewMode === 'kanban' 
                    ? 'bg-white dark:bg-slate-600 text-slate-900 dark:text-slate-100 shadow-sm' 
                    : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
                }`}
                data-testid="kanban-view-btn"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                </svg>
                <span className="hidden sm:inline">Kanban</span>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                  viewMode === 'list' 
                    ? 'bg-white dark:bg-slate-600 text-slate-900 dark:text-slate-100 shadow-sm' 
                    : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
                }`}
                data-testid="list-view-btn"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                <span className="hidden sm:inline">List</span>
              </button>
            </div>
            <Link to="/add-lead" className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-sm font-medium rounded-lg hover:bg-slate-800 dark:hover:bg-white transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Lead
            </Link>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KPICard title="Total Leads" value={totalLeads} trend={0} subValue="in pipeline" />
          <KPICard title="Total Value" value={formatCurrency(totalValue)} trend={0} subValue="deal value" />
          <KPICard title="Qualified" value={qualifiedLeads} trend={0} subValue="leads qualified" />
          <KPICard title="Won" value={wonLeads} trend={0} subValue="deals closed" />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Scope tabs — All / Mine / My Team */}
          <div className="flex items-center bg-slate-100 dark:bg-slate-700 rounded-lg p-1" data-testid="leads-scope-tabs">
            <button
              type="button"
              onClick={() => setScope('all')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${scope === 'all' ? 'bg-white dark:bg-slate-600 text-slate-900 dark:text-slate-100 shadow-sm' : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'}`}
              data-testid="scope-tab-all"
            >
              All Leads
            </button>
            <button
              type="button"
              onClick={() => setScope('mine')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${scope === 'mine' ? 'bg-white dark:bg-slate-600 text-slate-900 dark:text-slate-100 shadow-sm' : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'}`}
              data-testid="scope-tab-mine"
            >
              My Leads
            </button>
            {isManagerOrAbove && (
              <button
                type="button"
                onClick={() => setScope('team')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${scope === 'team' ? 'bg-white dark:bg-slate-600 text-slate-900 dark:text-slate-100 shadow-sm' : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'}`}
                data-testid="scope-tab-team"
              >
                My Team
              </button>
            )}
          </div>

          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search leads..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-400"
              data-testid="search-leads-input"
            />
          </div>
          <select 
            value={industryFilter} 
            onChange={(e) => setIndustryFilter(e.target.value)} 
            className="px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-slate-400"
            data-testid="industry-filter"
          >
            <option value="all">All Industries</option>
            {industries.map(i => <option key={i} value={i}>{i}</option>)}
          </select>
          <select 
            value={sourceFilter} 
            onChange={(e) => setSourceFilter(e.target.value)} 
            className="px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-slate-400"
            data-testid="source-filter"
          >
            <option value="all">All Sources</option>
            {sources.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {/* Content Area - Kanban or List */}
      {viewMode === 'kanban' ? (
        /* Kanban Board */
        <div className="flex-1 overflow-x-auto overflow-y-hidden p-4 lg:p-6">
          <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd}>
            <div className="flex gap-4 h-full min-w-max">
              {LEAD_STAGES.map(stage => (
                <LeadColumn
                  key={stage.id}
                  stage={stage}
                  leads={leadsByStage[stage.id] || []}
                  onLeadClick={handleLeadClick}
                  onAddOpportunity={handleAddOpportunity}
                  onQuickLog={(ctx) => setQuickLogContext(ctx)}
                  isOver={overId === stage.id}
                  meta={stageMeta[stage.id]}
                  onLoadMore={loadMoreStage}
                  isLoadingMore={loadingStage === stage.id}
                />
              ))}
            </div>
            <DragOverlay>
              {activeLead && <div className="rotate-3"><LeadCard lead={activeLead} isDragging={true} onClick={() => {}} onAddOpportunity={() => {}} onQuickLog={() => {}} /></div>}
            </DragOverlay>
          </DndContext>
        </div>
      ) : (
        /* List View — Server-side paginated */
        <div className="flex-1 overflow-auto p-4 lg:p-6">
          <LeadsListView 
            leads={listPagination ? listLeads : filteredLeads} 
            onLeadClick={handleLeadClick} 
            onAddOpportunity={handleAddOpportunity}
            onQuickLog={(ctx) => setQuickLogContext(ctx)}
            stages={LEAD_STAGES}
            formatCurrency={formatCurrency}
            onDeleteLead={handleDeleteLead}
            onBulkDelete={handleBulkDeleteLeads}
          />
          {listPagination && (
            <Pagination
              pagination={listPagination}
              onPageChange={(p) => setListPage(p)}
              onLimitChange={(l) => { setListLimit(l); setListPage(1); }}
            />
          )}
        </div>
      )}

      {/* Add Opportunity Modal */}
      {showAddOppModal && selectedLeadForOpp && (
        <AddOpportunityModal
          lead={selectedLeadForOpp}
          onClose={() => { setShowAddOppModal(false); setSelectedLeadForOpp(null); }}
          onSuccess={handleOppCreated}
        />
      )}

      {/* Drawer */}
      <LeadDrawer lead={selectedLead} isOpen={drawerOpen} onClose={() => { setDrawerOpen(false); setSelectedLead(null); }} onUpdate={handleLeadUpdate} stages={LEAD_STAGES} />

      {/* Quick Activity Log Modal */}
      {quickLogContext && (
        <QuickActivityLog
          context={quickLogContext}
          onClose={() => setQuickLogContext(null)}
          onSuccess={() => { setQuickLogContext(null); fetchLeads(); }}
        />
      )}

      <style>{`
        @keyframes slide-in-right { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .animate-slide-in-right { animation: slide-in-right 0.3s ease-out; }
        .line-clamp-1 { display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }
      `}</style>
    </div>
  );
}

// Add Opportunity Modal Component
function AddOpportunityModal({ lead, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: lead?.organization || lead?.name || '',
    deal_value: lead?.deal_value || '',
    stage: 'qualification',
    probability: 10,
    expected_close_date: '',
    priority: 'medium',
    description: '',
    next_step: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const STAGE_PROBABILITIES = {
    qualification: 10,
    discovery: 20,
    meeting: 40,
    proposal: 60,
    negotiation: 80
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const payload = {
        name: formData.name,
        deal_value: parseFloat(formData.deal_value) || 0,
        stage: formData.stage,
        expected_close_date: formData.expected_close_date || null,
        priority: formData.priority,
        description: formData.description || '',
        next_step: formData.next_step || '',
      };

      // Atomic convert (iteration 74): backend will flip lead.status to 'converted'.
      await axios.post(`${API}/leads/${lead.id}/convert`, payload);
      onSuccess();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Failed to convert lead');
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="add-opportunity-modal">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-slate-800 px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Add Opportunity
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              From Lead: <span className="font-medium text-slate-700 dark:text-slate-300">{lead.name}</span>
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">
            <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Opportunity Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
              placeholder="e.g., TechCorp - Contact Center License"
              className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              data-testid="opp-modal-name"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Deal Value (₹) *</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
                <input
                  type="number"
                  value={formData.deal_value}
                  onChange={(e) => setFormData(prev => ({ ...prev, deal_value: e.target.value }))}
                  required
                  placeholder="500000"
                  className="w-full pl-8 pr-4 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  data-testid="opp-modal-value"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Stage</label>
              <select
                value={formData.stage}
                onChange={(e) => {
                  const stage = e.target.value;
                  setFormData(prev => ({ ...prev, stage, probability: STAGE_PROBABILITIES[stage] || prev.probability }));
                }}
                className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                data-testid="opp-modal-stage"
              >
                <option value="qualification">Qualification</option>
                <option value="discovery">Discovery</option>
                <option value="meeting">Meeting</option>
                <option value="proposal">Proposal</option>
                <option value="negotiation">Negotiation</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Probability (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                value={formData.probability}
                onChange={(e) => setFormData(prev => ({ ...prev, probability: e.target.value }))}
                className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                data-testid="opp-modal-probability"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Expected Close</label>
              <input
                type="date"
                value={formData.expected_close_date}
                onChange={(e) => setFormData(prev => ({ ...prev, expected_close_date: e.target.value }))}
                className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                data-testid="opp-modal-date"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Priority</label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
              className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              data-testid="opp-modal-priority"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Next Step</label>
            <input
              type="text"
              value={formData.next_step}
              onChange={(e) => setFormData(prev => ({ ...prev, next_step: e.target.value }))}
              placeholder="e.g., Schedule discovery call"
              className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white rounded-lg transition-colors flex items-center gap-2"
              data-testid="opp-modal-submit"
            >
              {saving ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Create Opportunity
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
